// Room client (PLAN §5.5). Sends commands, applies what comes back, and
// notices when its own state diverges from the server's.
//
// Reconnect and spectate come for free because joining replays the room's log
// from a fresh game — laptops sleep during demos and deploys restart the
// process, and both become non-events.

import type { GameStore } from "src/engine/game/GameStore";
import type { Command } from "src/engine/rules/commands";
import {
  buildsAgree,
  type Occupant,
  type Seat,
  type ServerMessage,
  socketUrl
} from "./protocol";

/**
 * `offline` means there is no room at all — local play. `failed` means we
 * asked to join one and were refused.
 *
 * They used to be the same value, which made a rejected join look exactly like
 * a hot-seat game: the board came up as normal, moves applied locally, and the
 * player was in a phantom game no opponent would ever see. The URL is the
 * sharing mechanism, so a stale or mistyped link lands there.
 */
export type NetStatus =
  | "offline"
  | "connecting"
  | "online"
  | "desynced"
  /**
   * This tab is running a different build from the server.
   *
   * **Not a desync**, though it produces one: both sides run the same reducer
   * over the same log, so the only way they can disagree is by running
   * different *copies* of it — which is the normal state of affairs for a few
   * minutes after every deploy, because a browser holds its bundle until it is
   * reloaded. Told apart from a real determinism bug because the two call for
   * opposite responses: one is "press reload", the other is "go and find the
   * bug", and presenting the first as the second sends somebody hunting.
   */
  | "stale"
  | "failed";

export interface NetState {
  status: NetStatus;
  room?: string;
  seat?: Seat;
  /** Why the server refused, when status is "failed". */
  reason?: string;
  /** Set when the client and server computed different states from one log. */
  desync?: { seq: number; ours: number; theirs: number };
  /** The server's build, when it is not ours. Shown so the two are comparable. */
  serverBuild?: string;
  /** Your name as the server cleaned it — not necessarily what you asked for. */
  name?: string;
  /** Everyone in the room. Replaced whole on every arrival and departure. */
  occupants?: Occupant[];
}

const RECONNECT_MS = 1500;

export class RoomClient {
  private socket?: WebSocket;
  private seq = 0;
  private closed = false;
  private listeners = new Set<() => void>();
  private net: NetState = { status: "offline" };

  constructor(
    private store: GameStore,
    private room: string,
    private seat?: Seat,
    /** What to call you. The server clamps it and may hand back another. */
    private name?: string
  ) {}

  getState = (): NetState => this.net;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private update(patch: Partial<NetState>): void {
    this.net = { ...this.net, ...patch };
    for (const listener of this.listeners) listener();
  }

  connect(): void {
    this.closed = false;
    this.update({ status: "connecting" });

    const socket = new WebSocket(socketUrl(window.location));
    this.socket = socket;

    socket.onopen = () => {
      socket.send(
        // Our build goes up as well as the server's coming down, so the
        // server can see the skew in its own logs rather than only the tab
        // that is suffering from it.
        JSON.stringify({
          t: "join",
          room: this.room,
          seat: this.seat,
          name: this.name,
          build: __BUILD_ID__
        })
      );
    };

    socket.onmessage = (event) => this.receive(JSON.parse(event.data) as ServerMessage);

    socket.onclose = () => {
      if (this.closed) return;
      this.update({ status: "connecting" });
      // A dropped socket is normal — a slept laptop, a redeploy. Rejoining
      // replays the log, so no state is lost by reconnecting.
      setTimeout(() => this.connect(), RECONNECT_MS);
    };
  }

  close(): void {
    this.closed = true;
    this.socket?.close();
    this.update({ status: "offline" });
  }

  /**
   * Send a command. It is applied locally first so the board responds
   * immediately, but the server is the authority: if it rejects, the local
   * state is rebuilt from the log we know it accepted.
   */
  send(command: Command): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
    this.seq++;
    if (!this.store.dispatch(command)) return false;
    this.socket.send(
      JSON.stringify({
        t: "cmd",
        seq: this.seq,
        command,
        hash: this.store.getSnapshot().hash
      })
    );
    return true;
  }

  /**
   * Ask the room to take back your last action (§29 U3).
   *
   * **Not optimistic, unlike `send`.** A refused optimistic undo would replay
   * *forward* again, which is the expensive visible snap a rejection costs;
   * and the button is already greyed by the same `planUndo` the server runs,
   * so the round-trip is the only thing a press pays for. Two presses before
   * the reply are two undos, which is what two presses mean.
   */
  undo(): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify({ t: "undo" }));
    return true;
  }

  /**
   * Our board against the server's, and what a difference means.
   *
   * With an authoritative server this is not an attack — it means the client
   * and server computed different states from the same log. **Which is a
   * determinism bug only if the two are the same build.** Otherwise it is an
   * old tab, and the fix is a reload rather than an investigation; reporting
   * the commonest cause as the rarest one is how a deploy turns into an
   * afternoon.
   */
  private compare(seq: number, theirs: number): void {
    const ours = this.store.getSnapshot().hash;
    if (ours === theirs) return;
    const stale = !buildsAgree(__BUILD_ID__, this.net.serverBuild);
    this.update({ status: stale ? "stale" : "desynced", desync: { seq, ours, theirs } });
  }

  private receive(message: ServerMessage): void {
    switch (message.t) {
      case "joined":
        this.store.load(message.log);
        this.seq = message.seq;
        // Keep the seat, so a reconnect returns to the same side rather than
        // being handed whichever one happens to be free.
        this.seat = message.seat === "spectator" ? this.seat : message.seat;
        // **Before anything is played.** A tab that has been open across a
        // deploy runs the old rules against the new server, and every command
        // from here would hash differently — so say so now rather than letting
        // the first move look like a determinism bug.
        this.update({
          status: buildsAgree(__BUILD_ID__, message.build) ? "online" : "stale",
          room: message.room,
          seat: message.seat,
          name: message.name,
          serverBuild: message.build
        });
        break;

      case "roster":
        this.update({ occupants: message.occupants });
        break;

      case "cmd": {
        const known = this.store.getSnapshot().log.length;
        // Our own commands come back to us; we already applied them, and the
        // hash comparison below is what makes that safe.
        if (message.seq > known) this.store.dispatch(message.command);
        this.compare(message.seq, message.hash);
        break;
      }

      case "undo": {
        // The outgoing counter tracks the log's length, or the next
        // `rejected` rebuilds from the wrong place.
        this.seq = message.seq;
        try {
          this.store.applyUndo(message.dropped);
        } catch (error) {
          // A refusal here is a desync, not an orphan: the server's cut
          // replayed there. The hash is unchanged, so `compare` below sees the
          // mismatch and says which kind — stale build, or genuinely diverged.
          console.warn(`the room's undo does not replay here: ${String(error)}`);
        }
        this.compare(message.seq, message.hash);
        break;
      }

      case "undo-refused":
        // Nothing to rebuild: an undo request applied nothing optimistically.
        // Said out loud even so, because the button was offered.
        console.warn(`the server refused the undo: ${message.reason}`);
        break;

      case "rejected": {
        // The server refused it, so our optimistic apply was wrong. Rebuild
        // from the log rather than trying to undo one command.
        //
        // **And say so, because this is the expensive one.** A rebuild is a
        // replay of every command from the base state, and the board visibly
        // snaps backwards to whatever the server thinks is true — so a refusal
        // is not a quiet correction, it is the thing a player reports as lag.
        // It was silent at both ends: nothing on the server wrote it down
        // (`server/refusals.ts` does now) and nothing here did either, so the
        // only evidence was the feeling.
        const replayed = Math.max(0, message.seq - 1);
        console.warn(
          `server refused command ${message.seq}: ${message.reason} — ` +
            `rebuilding the board from ${replayed} command(s)`
        );
        this.store.load(this.store.getSnapshot().log.slice(0, replayed));
        break;
      }

      case "error":
        // The server refused us outright. Say so and stop pretending to be a
        // game: anything the player does from here reaches nobody.
        this.closed = true;
        this.socket?.close();
        this.update({ status: "failed", reason: message.reason });
        break;
    }
  }
}
